import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from './ui/tooltip';
import {
  Building2,
  Globe,
  RefreshCw,
  Info,
  Loader2,
  ChevronDown,
  Check
} from 'lucide-react';
import clientService from '../services/clientService';
import authService from '../services/authService';
import useDebounce from '../hooks/useDebounce';

/**
 * ClientSelector Component
 * Provides a dropdown to switch between global view and client-specific views
 * with real-time search and caching capabilities
 */
const ClientSelector = ({
  selectedClientId = null,
  onClientChange,
  showGlobalOption = true,
  disabled = false,
  size = 'medium',
  variant = 'outlined',
  label = '选择客户',
  placeholder = '搜索客户或选择全部',
  helperText = '',
  error = false,
  required = false,
  compact = false,
  className = '',
  selectedClientName = '',
  ...props
}) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [loadError, setLoadError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Track ongoing fetch requests to prevent duplicates
  const fetchingClientRef = useRef(new Set());

  // Check if current user is admin
  const isAdmin = authService.isAdmin();

  // Debounce search input
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');

      // Load more clients to ensure we have a comprehensive list
      const response = await clientService.getClients(1, 200, 'name', 'asc', { active: true });

      if (!response || !Array.isArray(response.clients)) {
        throw new Error('Invalid response format from client service');
      }

      // Filter out confidential clients for non-admin users
      const filteredClients = isAdmin
        ? response.clients
        : response.clients.filter(client => !client.is_confidential);

      setClients(filteredClients);
      setRetryCount(0); // Reset retry count on success
    } catch (error) {
      console.error('Failed to load clients:', error);

      if (retryCount < MAX_RETRIES) {
        // Auto-retry with exponential backoff
        const delay = 1000 * Math.pow(2, retryCount); // 1s, 2s, 4s
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          loadClients();
        }, delay);
        setLoadError(`加载失败，正在重试... (${retryCount + 1}/${MAX_RETRIES})`);
      } else {
        setLoadError('无法加载客户列表，请检查网络连接后手动重试');
        setClients([]);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount, isAdmin]);

  const searchClients = useCallback(async (query) => {
    try {
      setSearchLoading(true);

      // Request more results when searching to ensure we find clients across all pages
      const response = await clientService.searchClients(query, 1, 100);

      if (!response || !Array.isArray(response.clients)) {
        console.warn('Invalid search response format, keeping existing results');
        return;
      }

      // Filter out confidential clients for non-admin users
      const filteredClients = isAdmin
        ? response.clients
        : response.clients.filter(client => !client.is_confidential);

      // If no results, do not mutate the input or trigger placeholder searches
      if (filteredClients.length === 0) {
        setClients([]);
      } else {
        setClients(filteredClients);
      }
    } catch (error) {
      console.error('Failed to search clients:', error);
      // Don't show error for search failures, just keep existing results
    } finally {
      setSearchLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, loadClients]);

  // Load initial clients
  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Search clients when debounced search value changes
  useEffect(() => {
    const q = (debouncedSearchValue || '').trim();
    const globalLabel = '全局视图 (所有客户)';

    // Avoid triggering search with placeholder text, empty value, or specific labels
    if (q && q !== (placeholder || '').trim() && q !== globalLabel) {
      // Check if this is the selected client's name (avoid searching for already selected)
      if (selectedClientId && selectedClientId !== 'global' && selectedClientName) {
        if (q === selectedClientName) {
          return; // Don't search for the already selected client name
        }
      }
      searchClients(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue, searchClients, placeholder, selectedClientId, selectedClientName]);

  // Create options array with global option if enabled
  const options = useMemo(() => {
    const clientOptions = clients.map(client => ({
      id: client._id || client.client_id,
      label: `${client.name || client.client_name}${client.is_confidential && isAdmin ? ' 🔒' : ''}`,
      type: 'client',
      code: client.client_code,
      status: client.status,
      data: client,
      isConfidential: client.is_confidential
    }));

    // Include the selected client as a temporary option if it's not already in the list
    if (selectedClientId && selectedClientName && !selectedClientId.startsWith('legacy-')) {
      const isClientInList = clients.some(c => (c._id || c.client_id) === selectedClientId);
      if (!isClientInList) {
        clientOptions.unshift({
          id: selectedClientId,
          label: selectedClientName,
          type: 'client',
          code: '',
          status: 'active',
          data: null,
          isTemporary: true
        });
      }
    }

    if (showGlobalOption) {
      return [
        {
          id: 'global',
          label: '全局视图 (所有客户)',
          type: 'global',
          code: 'GLOBAL',
          status: 'active',
          data: null
        },
        ...clientOptions
      ];
    }

    return clientOptions;
  }, [clients, showGlobalOption, isAdmin, selectedClientId, selectedClientName]);

  // Find selected option
  const selectedOption = useMemo(() => {
    if (!selectedClientId) return null;

    // Handle legacy client IDs
    if (selectedClientId.startsWith('legacy-')) {
      return {
        id: selectedClientId,
        label: selectedClientName || '历史客户 (已编辑)',
        type: 'legacy',
        code: 'LEGACY',
        status: 'active',
        stats: null,
        data: null
      };
    }

    // Try to find in loaded options first
    const foundOption = options.find(option => option.id === selectedClientId);
    if (foundOption) {
      return foundOption;
    }

    // If not found but we have a name, create a temporary option
    if (selectedClientName) {
      return {
        id: selectedClientId,
        label: selectedClientName,
        type: 'client',
        code: '',
        status: 'active',
        stats: null,
        data: null,
        isTemporary: true
      };
    }

    return null;
  }, [selectedClientId, options, selectedClientName]);

  // Sync inputValue with selected option on initial load or when selection changes externally
  const prevSelectedOptionRef = useRef(null);
  useEffect(() => {
    if (selectedOption && selectedOption !== prevSelectedOptionRef.current) {
      setInputValue(selectedOption.label || '');
    } else if (!selectedOption && prevSelectedOptionRef.current) {
      setInputValue('');
    }
    prevSelectedOptionRef.current = selectedOption;
  }, [selectedOption]);

  // Ensure the selected client is present in options; fetch by ID if missing
  useEffect(() => {
    const ensureSelectedLoaded = async () => {
      const id = selectedClientId;
      if (!id || id === 'global' || id.startsWith('legacy-')) return;

      // Check if already fetching this client
      if (fetchingClientRef.current.has(id)) return;

      // Use setClients callback to check if client exists without adding clients to dependencies
      setClients(prev => {
        const exists = prev.some(c => (c._id || c.client_id) === id);
        if (!exists && !fetchingClientRef.current.has(id)) {
          // Mark as fetching to prevent duplicate requests
          fetchingClientRef.current.add(id);

          // Fetch the client asynchronously
          clientService.getClient(id).then(resp => {
            const client = resp.client || resp;
            if (client && (client._id || client.client_id)) {
              setClients(prevClients => {
                // Final check to prevent duplicates
                const alreadyExists = prevClients.some(c => (c._id || c.client_id) === id);
                if (alreadyExists) {
                  return prevClients;
                }
                return [...prevClients, client];
              });
            }
          }).catch(e => {
            // Silently ignore if client not found; keep UI usable
            console.warn('Client not found for selectedClientId:', id);
          }).finally(() => {
            // Remove from fetching set
            fetchingClientRef.current.delete(id);
          });
        }
        return prev; // Return unchanged for now
      });
    };
    ensureSelectedLoaded();
  }, [selectedClientId]);

  const handleChange = useCallback((option) => {
    const clientId = option?.id || null;
    const clientData = option?.type === 'client' ? option.data : null;

    onClientChange?.(clientId, clientData);
    setIsOpen(false);
    setSearchValue('');
    setInputValue(option?.label || '');
  }, [onClientChange]);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchValue(newValue);
    if (!isOpen) setIsOpen(true);
  }, [isOpen]);

  const handleRefresh = useCallback(() => {
    setSearchValue('');
    setInputValue('');
    setRetryCount(0);
    loadClients();
  }, [loadClients]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Support both mousedown and click for headless browser compatibility
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Filter options based on input
  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return options;
    const search = inputValue.toLowerCase();
    return options.filter(option =>
      option.label.toLowerCase().includes(search) ||
      (option.code && option.code.toLowerCase().includes(search))
    );
  }, [options, inputValue]);

  const renderOption = (option) => (
    <div
      key={option.id}
      className={`flex items-center px-3 py-2 cursor-pointer hover:bg-muted ${
        selectedOption?.id === option.id ? 'bg-primary/10' : ''
      }`}
      onClick={() => handleChange(option)}
    >
      <div className="flex items-center mr-3">
        {option.type === 'global' ? (
          <Globe className="h-5 w-5 text-primary" />
        ) : option.type === 'legacy' ? (
          <Building2 className="h-5 w-5 text-warning" />
        ) : (
          <Building2 className="h-5 w-5 text-muted-foreground" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className={`text-sm ${option.type === 'global' ? 'font-semibold' : ''}`}>
          {option.label}
        </div>

        {option.code && option.code !== 'GLOBAL' && (
          <div className="text-xs text-muted-foreground">
            编码: {option.code}
          </div>
        )}
      </div>

      {selectedOption?.id === option.id && (
        <Check className="h-4 w-4 text-primary ml-2" />
      )}

      {option.status && option.status !== 'active' && (
        <Badge variant="outline" className="ml-2 text-warning border-yellow-500">
          {option.status}
        </Badge>
      )}
    </div>
  );

  if (compact) {
    return (
      <div className={`relative ${className}`} ref={containerRef} {...props}>
        <div className="relative">
          <Input
            value={isOpen ? inputValue : (selectedOption?.label || '')}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className={`pr-20 ${error || loadError ? 'border-red-500' : ''}`}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {(loading || searchLoading) && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleRefresh}
              disabled={loading || searchLoading || disabled}
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-80 overflow-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {debouncedSearchValue ? "未找到匹配的客户" : "暂无客户"}
              </div>
            ) : (
              filteredOptions.map(renderOption)
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={className} ref={containerRef} {...props}>
      {/* Client context info */}
      {selectedOption && (
        <div className="mb-2 flex items-center gap-2">
          <div className="relative">
            {selectedOption.type === 'global' ? (
              <Globe className="h-5 w-5 text-primary" />
            ) : selectedOption.type === 'legacy' ? (
              <Building2 className="h-5 w-5 text-warning" />
            ) : (
              <Building2 className="h-5 w-5 text-muted-foreground" />
            )}
            <Badge className="absolute -top-2 -right-2 h-4 min-w-4 text-xs px-1" variant="default">
              {selectedOption.type === 'global' ? options.length - 1 : '1'}
            </Badge>
          </div>

          <span className="text-sm text-muted-foreground">
            当前上下文: {selectedOption.label}
            {selectedOption.stats && (
              <> • {selectedOption.stats.total_products || 0} 产品 • 库存 {selectedOption.stats.total_inventory || 0}</>
            )}
          </span>

          {selectedOption.type === 'client' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  客户特定视图: 显示该客户的专属产品属性和库存
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}

      <div className="relative">
        <div className="relative">
          {selectedOption && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              {selectedOption.type === 'global' ? (
                <Globe className="h-4 w-4 text-primary" />
              ) : selectedOption.type === 'legacy' ? (
                <Building2 className="h-4 w-4 text-warning" />
              ) : (
                <Building2 className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          )}
          <Input
            value={isOpen ? inputValue : (selectedOption?.label || '')}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className={`${selectedOption ? 'pl-10' : ''} pr-20 ${error || loadError ? 'border-red-500' : ''}`}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {(loading || searchLoading) && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={handleRefresh}
                    disabled={loading || searchLoading || disabled}
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>刷新客户列表</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-80 overflow-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {debouncedSearchValue ? "未找到匹配的客户" : "暂无客户"}
              </div>
            ) : (
              filteredOptions.map(renderOption)
            )}
          </div>
        )}
      </div>

      {loadError && (
        <Alert variant="destructive" className="mt-2">
          <AlertDescription className="flex items-center gap-2">
            {loadError}
            <Button variant="ghost" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default ClientSelector;
